// fastTrackAddon.js
// Priority Physician Review — the paid add-on ("Physician Fast Track").
//
// Everything about the product is resolved at runtime from /api/get-all-products.
// The ONLY hardcoded value in this feature is the internal_id below: the product's
// ObjectId differs per environment (it exists on DEV, not yet on RxPros production),
// and its price lives on the product's terms-0 pricing plan.
//
// It is NOT part of checkout. The pre-ticked box that used to add it to the cart is
// gone — it cost conversion on the order itself — and no checkout payload carries
// this fee any more. It is offered after the order is captured and charged
// separately: see FastTrackUpsellModal.vue and fastTrackUpsell.js.
//
// `is_active` on the product is the ONLY on/off switch. Flip it false in the catalog
// and the offer disappears with no deploy.

export const FAST_TRACK_INTERNAL_ID = 'FAST_TRACK';

/**
 * Case-insensitive and trimmed on purpose. `internal_id` is a free-text field
 * edited by hand in the catalog, and it has already flipped from `fast_track`
 * to `FAST_TRACK` once — an exact match silently turns the add-on off across
 * every checkout with no error anywhere.
 */
export const isFastTrackProduct = (product) =>
    typeof product?.internal_id === 'string' &&
    product.internal_id.trim().toUpperCase() === FAST_TRACK_INTERNAL_ID;

/**
 * Resolve the add-on from a catalog array. Returns { product, plan } or null.
 * null means the add-on is not offerable — callers must make no offer at all.
 *
 * is_active is the only switch, on the product and on its pricing plan. `terms`
 * is not a condition: the add-on is offered when the record is active and is not
 * offered when it is not, whatever term the plan happens to carry.
 *
 * There is deliberately no fallback price and no fallback product id.
 */
export const resolveFastTrackAddon = (products) => {
    if (!Array.isArray(products)) return null;

    const product = products.find(p => isFastTrackProduct(p) && p.is_active);
    if (!product) return null;

    const plan = (product.pricing_plans || []).find(p => p && p.is_active && p.price != null);
    if (!plan) return null;

    return { product, plan };
};
